import { forwardRef, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import { Tooltip } from '@/components/ui/tooltip';
import { cls } from '@/utils/cls';
import { label } from '@/utils/format/label';

import { CreateAlertTask } from '../api/mutations/create-alert-task';
import { CreateStandardTask } from '../api/mutations/create-standard-task';
import { UpdateTask } from '../api/mutations/update-task';
import type { TaskPreview } from '../entities/task-preview';
import { TaskStatus } from '../entities/task-status';
import type { DropData } from '../types/drop-data';

import { TaskCard } from './task-card';
import { TaskCardCreate } from './task-card-create';

export type TasksColumnProps = {
  status: TaskStatus;
  tasks: TaskPreview[];
  draggable?: boolean;
  onTaskOpen: (taskId: string) => void;
  onTaskUpdate: (input: UpdateTask.Input, task: TaskPreview) => Promise<void>;
  onStandardTaskCreate?: (input: CreateStandardTask.Input) => Promise<void>;
  onAlertTaskCreate: (input: CreateAlertTask.Input) => Promise<void>;
  className?: string;
};

const TasksColumn = forwardRef<HTMLDivElement, TasksColumnProps>(function (
  {
    status,
    tasks,
    draggable,
    onTaskOpen,
    onTaskUpdate,
    onStandardTaskCreate,
    onAlertTaskCreate,
    className,
  },
  ref,
) {
  const createRef = useRef<HTMLFormElement>(null);
  const createShow = useBoolean(false);

  useOnClickOutside(createRef, createShow.setFalse);

  function showCreate() {
    createShow.setTrue();
    createRef.current?.querySelector('input')?.focus();
  }

  return (
    <div
      ref={ref}
      className={cls('flex size-full flex-col overflow-hidden', className)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={async function (e) {
        const drop = JSON.parse(
          e.dataTransfer.getData('text/plain'),
        ) as DropData;

        switch (drop.type) {
          case 'move-task': {
            const { task } = drop;
            onTaskUpdate({ status, id: task.id }, task);
            break;
          }
          case 'create-task-from-alert': {
            const { alertSuggestion } = drop;
            onAlertTaskCreate({ status, alertSuggestion });
            break;
          }
        }
      }}
      data-testid={`task-column-${status.toLowerCase().replace(' ', '-')}`}
    >
      <div className="w-full shrink-0 space-y-2">
        <div className="flex justify-between gap-2">
          <div className="flex cursor-default items-end gap-3 text-sm font-normal leading-normal">
            <span className="rounded font-semibold text-neutral-600">
              {status}
            </span>
            <Tooltip delay content={getTaskCountLabel(status, tasks.length)}>
              <span className="px-2 text-neutral-500">{tasks.length}</span>
            </Tooltip>
          </div>
          {onStandardTaskCreate ? (
            <button
              className={cls(
                'flex size-6 cursor-pointer flex-col items-center justify-center rounded-md hover:bg-neutral-500/10',
                { 'bg-neutral-500/10': createShow.value },
              )}
              onClick={() => showCreate()}
            >
              <PlusIcon className="size-5 font-bold text-neutral-500" />
            </button>
          ) : null}
        </div>

        <div className="h-1 w-full rounded bg-stone-300" />
      </div>

      <div className="size-full grow space-y-4 overflow-y-auto py-4">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            draggable={draggable}
            onClick={() => onTaskOpen(task.id)}
          />
        ))}
        {onStandardTaskCreate ? (
          <TaskCardCreate
            ref={createRef}
            className={cls({ hidden: !createShow.value })}
            onCreate={({ title }) => onStandardTaskCreate({ title, status })}
          />
        ) : null}
      </div>
    </div>
  );
});

function getTaskCountLabel(status: TaskStatus, count: number) {
  const taskLabel = label(['task', 'tasks'], count);
  switch (status) {
    case TaskStatus.OPEN:
      return `${count} open ${taskLabel}`;
    case TaskStatus.IN_PROGRESS:
      return `${count} ${taskLabel} in progress`;
    case TaskStatus.EXTERNAL_REVIEW:
      return `${count} ${taskLabel} under external review`;
    case TaskStatus.DONE:
      return `${count} done ${taskLabel}`;
    default:
      return `${count} ${taskLabel}`;
  }
}

TasksColumn.displayName = 'TasksColumn';
export { TasksColumn };
